/**
 * EditModuleDrawer (T8.1a F2) — admin edit form per module.
 *
 * Contracts (locked by tests):
 *   - code + is_core inputs read-only.
 *   - isActive checkbox disabled when isCore=true (FE safeguard mirroring BE 409).
 *   - Submit sends DIFF-ONLY body (FE-BE contract from D7).
 *   - 422 → field-level errors via fieldErrors state.
 *
 * Pattern: handcrafted form state (mirror CreateCouponModal.tsx) — no react-hook-form.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { IconPicker } from '@/components/modules/IconPicker';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { Drawer } from './Drawer';
import { Input } from './Input';
import { Select } from './Select';

export interface ModuleFull {
  id: string;
  code: string;
  name: string;
  description: string | null;
  icon: string | null;
  category: string | null;
  sortOrder: number | null;
  dependsOnModuleCodes: string[] | null;
  isCore: boolean | null;
  isActive: boolean | null;
  gcrecord: string | null;
  createdAt: string;
  updatedAt: string;
  /** T20.1: area fields from detail=full response */
  areaId?: string | null;
  areaName?: string | null;
  areaSortOrder?: number | null;
}

interface ModuleAreaItem {
  id: string;
  code: string;
  name: string;
  icon: string | null;
  color: string | null;
  sortOrder: number | null;
  templateCount: number;
}

interface ModuleAreasResponse {
  areas: ModuleAreaItem[];
}

export interface EditModuleDrawerProps {
  open: boolean;
  module: ModuleFull | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface ApiErrorShape {
  response?: {
    status?: number;
    data?: {
      error?: string;
      details?: { fieldErrors?: Record<string, string[]>; formErrors?: string[] };
    };
  };
}

export function EditModuleDrawer({ open, module, onClose, onSuccess }: EditModuleDrawerProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [areaId, setAreaId] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const areasQuery = useQuery({
    queryKey: ['admin', 'module-areas'],
    queryFn: async (): Promise<ModuleAreasResponse> => {
      const res = await api.get<ModuleAreasResponse>('/admin/module-areas');
      return res.data;
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  /** 403 = permission-gated endpoint; render select disabled */
  const areasBlocked =
    (areasQuery.error as { response?: { status?: number } } | null)?.response?.status === 403;
  const areas = areasQuery.data?.areas ?? [];

  useEffect(() => {
    if (!module) return;
    setName(module.name);
    setDescription(module.description ?? '');
    setIcon(module.icon ?? '');
    setSortOrder(module.sortOrder !== null ? String(module.sortOrder) : '');
    setIsActive(!!module.isActive);
    setAreaId(module.areaId ?? '');
    setFieldErrors({});
    setApiError(null);
  }, [module]);

  if (!module) return null;
  const isCore = !!module.isCore;

  function buildDiff(): Record<string, unknown> {
    if (!module) return {};
    const diff: Record<string, unknown> = {};
    if (name !== module.name) diff.name = name;
    const newDesc = description.length > 0 ? description : null;
    if (newDesc !== module.description) diff.description = newDesc;
    const newIcon = icon.length > 0 ? icon : null;
    if (newIcon !== module.icon) diff.icon = newIcon;
    const newSort = sortOrder.length > 0 ? Number(sortOrder) : null;
    if (newSort !== module.sortOrder) diff.sortOrder = newSort;
    if (isActive !== !!module.isActive) diff.isActive = isActive;
    // areaId: '' means cleared (null); compare to current module.areaId (undefined treated as null)
    const newAreaId = areaId.length > 0 ? areaId : null;
    const currentAreaId = module.areaId ?? null;
    if (newAreaId !== currentAreaId) diff.areaId = newAreaId;
    return diff;
  }

  async function handleSubmit() {
    setFieldErrors({});
    setApiError(null);

    const body = buildDiff();
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      await api.patch(`/admin/modules/${module!.code}`, body);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'modules'] });
      toast.show({
        variant: 'success',
        title: t('admin.modules.toasts.editSuccess'),
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      const e = err as ApiErrorShape;
      const status = e.response?.status;
      const fe = e.response?.data?.details?.fieldErrors;
      if (status === 422 && fe) {
        const flat: Record<string, string> = {};
        for (const [k, v] of Object.entries(fe)) {
          if (Array.isArray(v) && typeof v[0] === 'string') flat[k] = v[0];
        }
        setFieldErrors(flat);
      } else if (status === 409 && e.response?.data?.error === 'CANNOT_DEACTIVATE_CORE_MODULE') {
        setApiError(t('admin.modules.toasts.cannotDeactivateCore'));
      } else {
        setApiError(t('admin.modules.toasts.editError'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('admin.modules.modal.editTitle')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {t('admin.modules.modal.cancel')}
          </Button>
          <Button onClick={handleSubmit} loading={submitting}>
            {t('admin.modules.modal.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {apiError && (
          <div
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            {apiError}
          </div>
        )}

        <Input
          label={t('admin.modules.modal.fields.code')}
          value={module.code}
          disabled
          title={t('admin.modules.codeReadonlyTooltip')}
          readOnly
        />

        <Checkbox
          label={t('admin.modules.modal.fields.isCore')}
          checked={isCore}
          disabled
          title={t('admin.modules.isCoreReadonlyTooltip')}
          readOnly
          onChange={() => {
            // intentionally noop — read-only
          }}
        />

        <Input
          label={t('admin.modules.modal.fields.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={fieldErrors.name}
          required
        />

        <Input
          label={t('admin.modules.modal.fields.description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={fieldErrors.description}
        />

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-text-secondary">
            {t('admin.modules.modal.fields.icon')}
          </label>
          <IconPicker
            value={icon ? icon.charAt(0).toUpperCase() + icon.slice(1) : ''}
            onChange={(name) => setIcon(name)}
            ariaLabel={t('admin.modules.modal.fields.icon')}
          />
          {fieldErrors.icon && (
            <span className="text-xs text-red-600" role="alert">
              {fieldErrors.icon}
            </span>
          )}
        </div>

        <Select
          label={t('admin.modules.modal.fields.area')}
          value={areaId}
          onChange={(e) => setAreaId(e.target.value)}
          disabled={areasBlocked || areasQuery.isLoading}
          hint={areasBlocked ? t('admin.modules.modal.fields.areaBlockedHint') : undefined}
          options={[
            { value: '', label: t('admin.modules.modal.fields.areaEmpty') },
            ...areas.map((a) => ({ value: a.id, label: a.name })),
          ]}
          error={fieldErrors.areaId}
        />

        <Input
          label={t('admin.modules.modal.fields.sortOrder')}
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          error={fieldErrors.sortOrder}
        />

        <span title={isCore ? t('admin.modules.isActiveCoreDisabledTooltip') : undefined}>
          <Checkbox
            label={t('admin.modules.modal.fields.isActive')}
            checked={isActive}
            disabled={isCore}
            onChange={(e) => setIsActive(e.target.checked)}
          />
        </span>
      </div>
    </Drawer>
  );
}
